import { Box, Skeleton, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { useAppSocket } from '../../../providers/AppSocketContext';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

const TopSellingItemsBody = () => {
  const { topItems, currency } = useDashboard();
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

  if (topItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <DashboardSizedChart height={300} dynamicHeight={topItems.length * 40}>
      <BarChart
        data={topItems}
        layout="vertical"
        margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis type="number" tickFormatter={formatCurrency} />
        <YAxis
          type="category"
          dataKey="name"
          width={140}
          tick={{ fontSize: 11 }}
          tickFormatter={(v: string) => v.length > 22 ? v.substring(0, 20) + '…' : v}
        />
        <Tooltip
          formatter={(value: number, name: string) => [
            formatTooltipValue(value),
            name === 'total' ? t('common:dashboard.topItems.revenue') : t('common:dashboard.topItems.qty'),
          ]}
        />
        <Bar dataKey="total" fill={themeColor} radius={[0, 4, 4, 0]} name="total" />
      </BarChart>
    </DashboardSizedChart>
  );
};

const TopSellingItems = () => {
  const { topItems, sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.sales) {
    return (
      <DashboardWidget title={t('common:dashboard.topItems.title')} helpKey="topItems.title" chartHeight={420}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      title={t('common:dashboard.topItems.title')}
      helpKey="topItems.title"
      chartHeight={Math.max(300, topItems.length * 40)}
      scrollable
    >
      <TopSellingItemsBody />
    </DashboardWidget>
  );
};

export default TopSellingItems;